import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MapView from '../../components/MapView';
import { useRideStore } from '../../store/rideStore';
import { apiService } from '../../services/api';
import { wsService } from '../../services/websocket';
import { canAcceptRide, canArriveRide, canStartRide, normalizeRideStatus, RideStatus } from '../../constants/ride-status';
import { useAlertManager } from '../../components/AlertProvider';
import { DRIVER_EVENTS } from '../../constants/websocket-events';
import { MaterialCommunityIcons } from '../../utils/icons';
import { Colors } from '../../theme/colors';
import { getRouteDirections } from '../../utils/directions';
import { calculateBounds, boundsCenter, estimateZoom } from '../../utils/mapUtils';
import { getCurrentPosition } from '../../utils/geolocation';
import { calculateDistance, formatDistance, calculateETA, formatETA } from '../../utils/distance';
import './RideDetailsScreen.css';

const RideDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { rideId } = useParams<{ rideId: string }>();
  const { acceptRide, arriveRide, startRide, updateCurrentRide } = useRideStore();
  const { showAlert } = useAlertManager();
  const [ride, setRide] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [routePath, setRoutePath] = useState<Array<{ lat: number; lng: number }>>([]);
  const [driverLocation, setDriverLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [distanceToPickup, setDistanceToPickup] = useState<number | null>(null);
  const [etaToPickup, setEtaToPickup] = useState<number | null>(null);
  const [routeDistance, setRouteDistance] = useState<number | null>(null);
  const [routeEta, setRouteEta] = useState<number | null>(null);
  const locationIntervalRef = useRef<NodeJS.Timeout | null>(null);



  const startPoint = useMemo(() => ({
    lat: ride?.start_point?.latitude || 0,
    lng: ride?.start_point?.longitude || 0
  }), [ride?.start_point?.latitude, ride?.start_point?.longitude]);

  const endPoint = useMemo(() => ({
    lat: ride?.end_point?.latitude || 0,
    lng: ride?.end_point?.longitude || 0
  }), [ride?.end_point?.latitude, ride?.end_point?.longitude]);


  const mapCenter = useMemo(() => {
    const points: Array<{ lat: number; lng: number }> = [];

    if (driverLocation) {
      points.push(driverLocation);
    }
    if (startPoint.lat !== 0 && startPoint.lng !== 0) {
      points.push(startPoint);
    }
    if (endPoint.lat !== 0 && endPoint.lng !== 0) {
      points.push(endPoint);
    }

    if (points.length === 0) {
      return null;
    }

    if (points.length === 1) {
      return points[0];
    }

    const bounds = calculateBounds(points);
    if (!bounds) {

      const allLats = points.map(p => p.lat);
      const allLngs = points.map(p => p.lng);
      return {
        lat: allLats.reduce((a, b) => a + b, 0) / allLats.length,
        lng: allLngs.reduce((a, b) => a + b, 0) / allLngs.length,
      };
    }
    return boundsCenter(bounds);
  }, [startPoint, endPoint, driverLocation]);

  const mapZoom = useMemo(() => {
    const points: Array<{ lat: number; lng: number }> = [];

    if (driverLocation) {
      points.push(driverLocation);
    }
    if (startPoint.lat !== 0 && startPoint.lng !== 0) {
      points.push(startPoint);
    }
    if (endPoint.lat !== 0 && endPoint.lng !== 0) {
      points.push(endPoint);
    }

    if (points.length >= 2) {
      const bounds = calculateBounds(points);
      if (!bounds) {
        return 14;
      }
      return estimateZoom(bounds);
    }
    return 14;
  }, [startPoint, endPoint, driverLocation]);


  const mapMarkers = useMemo(() => {
    const markers: Array<{
      position: { lat: number; lng: number };
      title: string;
      color: string;
      icon?: google.maps.Icon;
    }> = [];


    if (driverLocation) {
      markers.push({
        position: driverLocation,
        title: 'Your Location',
        color: Colors.primary,
        icon: undefined, 
      });
    }


    if (startPoint.lat !== 0 && startPoint.lng !== 0) {
      markers.push({
        position: startPoint,
        title: 'Pickup Location',
        color: Colors.success,
        icon: undefined, 
      });
    }


    if (endPoint.lat !== 0 && endPoint.lng !== 0) {
      markers.push({
        position: endPoint,
        title: 'Dropoff Location',
        color: Colors.error,
        icon: undefined, 
      });
    }

    return markers;
  }, [startPoint, endPoint, driverLocation]);


  const polylines = useMemo(() => {
    const polylines: Array<{ path: Array<{ lat: number; lng: number }>; color: string; strokeWidth: number }> = [];


    if (driverLocation && startPoint.lat !== 0 && startPoint.lng !== 0) {

      polylines.push({ 
        path: [driverLocation, startPoint], 
        color: Colors.primary, 
        strokeWidth: 5 
      });
    }


    if (startPoint.lat !== 0 && startPoint.lng !== 0 && endPoint.lat !== 0 && endPoint.lng !== 0) {
      if (routePath.length > 0) {

        polylines.push({ 
          path: routePath, 
          color: Colors.success, 
          strokeWidth: 4 
        });
      } else {

        polylines.push({ 
          path: [startPoint, endPoint], 
          color: Colors.success, 
          strokeWidth: 4 
        });
      }
    }

    return polylines;
  }, [routePath, startPoint, endPoint, driverLocation]);


  useEffect(() => {
    const updateDriverLocation = () => {
      getCurrentPosition(
        (position: any) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setDriverLocation(loc);
        },
        (error: any) => {
          console.warn('Failed to get driver location:', error);
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    };


    updateDriverLocation();


    locationIntervalRef.current = setInterval(updateDriverLocation, 5000);

    return () => {
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadRide();

    const handleRideUpdated = (data: any) => {
      const rideData = data.ride || data;
      if (rideData.id?.toString() === rideId?.toString()) {
        const transformedRide = {
          ...rideData,
          status: normalizeRideStatus(rideData.status),
        };
        setRide(transformedRide);
        updateCurrentRide(transformedRide);

        if (normalizeRideStatus(transformedRide.status) === RideStatus.STARTED) {
          navigate('/active-ride');
        }
      }
    };

    wsService.on(DRIVER_EVENTS.RIDE_UPDATED, handleRideUpdated);
    wsService.on(DRIVER_EVENTS.RIDE_STARTED, handleRideUpdated);

    return () => {
      wsService.off(DRIVER_EVENTS.RIDE_UPDATED, handleRideUpdated);
      wsService.off(DRIVER_EVENTS.RIDE_STARTED, handleRideUpdated);
    };
  }, [rideId, navigate, updateCurrentRide]);


  useEffect(() => {
    if (!ride) return;

    const calculateRoutes = async () => {

      if (driverLocation && startPoint.lat !== 0 && startPoint.lng !== 0 && window.google) {
        try {
          const driverToPickupRoute = await getRouteDirections(
            { latitude: driverLocation.lat, longitude: driverLocation.lng },
            { latitude: startPoint.lat, longitude: startPoint.lng }
          );

          if (driverToPickupRoute && driverToPickupRoute.path && driverToPickupRoute.path.length > 0) {
            setDistanceToPickup(driverToPickupRoute.distance);
            setEtaToPickup(driverToPickupRoute.duration);
          } else {

            const distToPickup = calculateDistance(driverLocation.lat, driverLocation.lng, startPoint.lat, startPoint.lng);
            setDistanceToPickup(distToPickup);
            setEtaToPickup(calculateETA(distToPickup));
          }
        } catch (error) {
          console.warn('Route calculation failed:', error);

          if (startPoint.lat !== 0 && startPoint.lng !== 0) {
            const distToPickup = calculateDistance(driverLocation.lat, driverLocation.lng, startPoint.lat, startPoint.lng);
            setDistanceToPickup(distToPickup);
            setEtaToPickup(calculateETA(distToPickup));
          }
        }
      }


      if (startPoint.lat !== 0 && startPoint.lng !== 0 && endPoint.lat !== 0 && endPoint.lng !== 0 && window.google) {
        try {
          const pickupToDropoffRoute = await getRouteDirections(
            { latitude: startPoint.lat, longitude: startPoint.lng },
            { latitude: endPoint.lat, longitude: endPoint.lng }
          );

          if (pickupToDropoffRoute && pickupToDropoffRoute.path && pickupToDropoffRoute.path.length > 0) {
            setRouteDistance(pickupToDropoffRoute.distance);
            setRouteEta(pickupToDropoffRoute.duration);


            const path = pickupToDropoffRoute.path.map(p => ({ lat: p.latitude, lng: p.longitude }));
            setRoutePath(path);
          } else {

            const dist = calculateDistance(startPoint.lat, startPoint.lng, endPoint.lat, endPoint.lng);
            setRouteDistance(dist);
            setRouteEta(calculateETA(dist));
            setRoutePath([startPoint, endPoint]);
          }
        } catch (error) {
          console.warn('Route calculation failed:', error);

          const dist = calculateDistance(startPoint.lat, startPoint.lng, endPoint.lat, endPoint.lng);
          setRouteDistance(dist);
          setRouteEta(calculateETA(dist));
          setRoutePath([startPoint, endPoint]);
        }
      }
    };

    calculateRoutes();
  }, [driverLocation, startPoint, endPoint, ride]);

  const loadRide = async () => {
    try {
      const data: any = await apiService.get(`/rides/${rideId}`);
      setRide(data);
      updateCurrentRide(data as any);
    } catch (error) {
      showAlert('Error', 'Failed to load ride details', [{ text: 'OK' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      const acceptedRide = await acceptRide(rideId!);

      updateCurrentRide(acceptedRide);

      await new Promise(resolve => setTimeout(resolve, 200));
      navigate('/active-ride');
    } catch (error: any) {
      showAlert('Error', error.message, [{ text: 'OK' }]);
    }
  };

  const handleArrive = async () => {
    try {
      await arriveRide(rideId!);
      showAlert('Arrived', 'You have arrived at pickup location', [{ text: 'OK' }]);
      loadRide();
    } catch (error: any) {
      showAlert('Error', error.message, [{ text: 'OK' }]);
    }
  };

  const handleStart = async () => {
    try {
      const startedRide = await startRide(rideId!);

      updateCurrentRide(startedRide);

      setTimeout(() => {
        navigate('/active-ride');
      }, 200);
    } catch (error: any) {
      showAlert('Error', error.message, [{ text: 'OK' }]);
    }
  };

  if (loading || !ride) {
    return <div className="ride-details-loading"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="ride-details-container">
      <MapView 
        center={mapCenter || undefined} 
        zoom={mapZoom} 
        markers={mapMarkers}
        polylines={polylines} 
      />

      <div className="ride-details-top-bar">
        <button className="icon-button" onClick={() => navigate('/')}>
          <MaterialCommunityIcons name="arrow-back" size={24} color={Colors.textPrimary} />
        </button>
        <div className="status-badge">
          <div className="status-dot" style={{ backgroundColor: Colors.primary }} />
          <span className="status-text">{normalizeRideStatus(ride.status)}</span>
        </div>
        <div style={{ width: 48 }} />
      </div>

      <div className="ride-details-bottom-card">
        <h2>Ride #{ride.ride_number}</h2>
        {ride.rider && (
          <div className="rider-info">
            <h3>Rider</h3>
            <p>{ride.rider.name}</p>
            <p>{ride.rider.phone}</p>
          </div>
        )}
        {distanceToPickup !== null && (
          <div className="distance-info">
            <div className="distance-item">
              <MaterialCommunityIcons name="map-marker-distance" size={20} color={Colors.primary} />
              <div className="distance-details">
                <strong>Distance to Pickup:</strong>
                <p>{formatDistance(distanceToPickup)}</p>
              </div>
            </div>
            {etaToPickup !== null && (
              <div className="distance-item">
                <MaterialCommunityIcons name="clock-outline" size={20} color={Colors.primary} />
                <div className="distance-details">
                  <strong>ETA to Pickup:</strong>
                  <p>{formatETA(etaToPickup)}</p>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="locations">
          <div className="location-item">
            <strong>Pickup:</strong>
            <p>{ride.origin_address || 'Pickup location'}</p>
          </div>
          <div className="location-item">
            <strong>Dropoff:</strong>
            <p>{ride.destination_address || 'Dropoff location'}</p>
          </div>
        </div>
        {routeDistance !== null && (
          <div className="route-info">
            <div className="route-item">
              <MaterialCommunityIcons name="route" size={20} color={Colors.success} />
              <div className="route-details">
                <strong>Route Distance:</strong>
                <p>{formatDistance(routeDistance)}</p>
              </div>
            </div>
            {routeEta !== null && (
              <div className="route-item">
                <MaterialCommunityIcons name="timer-outline" size={20} color={Colors.success} />
                <div className="route-details">
                  <strong>Route ETA:</strong>
                  <p>{formatETA(routeEta)}</p>
                </div>
              </div>
            )}
          </div>
        )}
        {ride.estimated_fare && (
          <div className="fare">
            <strong>Estimated Fare:</strong> ${ride.estimated_fare}
          </div>
        )}
        <div className="actions">
          {canAcceptRide(ride.status) && (
            <button className="accept-btn" onClick={handleAccept}>Accept Ride</button>
          )}
          {canArriveRide(ride.status) && (
            <button className="arrive-btn" onClick={handleArrive}>I've Arrived</button>
          )}
          {canStartRide(ride.status) && (
            <button className="start-btn" onClick={handleStart}>Start Ride</button>
          )}
          <button className="chat-btn" onClick={() => navigate(`/chat/${rideId}`)}>Chat with Rider</button>
          {canAcceptRide(ride.status) && (
            <button className="reject-btn" onClick={() => {
              const normalizedStatus = normalizeRideStatus(ride.status);
              if (normalizedStatus === RideStatus.SEARCHING_DRIVER) {
                // For SEARCHING_DRIVER status, we should reject the ride
                // This will remove it from the driver's view and allow another driver to accept
                navigate('/'); 
              } else {

                navigate('/'); 
              }
            }}>Cancel</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RideDetailsScreen;
